import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { isEmpty } from 'lodash-es'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { HTML_LINK_TARGET } from '../../../constants/html'
import {
  EMAIL_FOLDER_DEFAULT_VALUE,
  SUBMENU_ACCOUNT_PARAM_KEY,
  SUBMENU_PARAM_KEY,
} from '../../../constants/route'
import { LOCAL_STORAGE_MAILS_ACTIVE_ACCOUNT_KEY } from '../../../constants/storage'
import { useSubMenuActions } from '../../../hooks'
import { setStorageItem } from '../../../utils/storage'
import { Divider } from '../../Divider'
import { InputField } from '../../Input'
import {
  SubMenuAvailableItemsSection,
  SubMenuFoldersSection,
  SubMenuWrapper,
} from '../../SubMenu'
import { getEmailAccountsMenu, reconnectEmailAccount } from '../Email.api'

import {
  EMAIL_CLIENT,
  EMAIL_SUB_MENU_ACCOUNTS_MENU_NO_COUNT_PARAM,
  FILTER_NAME_ACCOUNT,
  FOLDER_ITEM_KEYS,
  ITEM_KEYS,
  QA_NAME_EMAIL_SUB_MENU,
  SEARCH_INPUT_HEIGHT,
} from './EmailSubMenu.constants'
import { useDefaultEmailParams } from './EmailSubMenu.hooks'
import {
  getSubMenuEmailsAccountsList,
  getSubMenuEmailsActiveValue,
  getSubMenuEmailsDefaultValue,
  getSubMenuEmailsSectionList,
  isSubMenuEmailNeedReconnect,
  subMenuEmailsFilterFolders,
} from './EmailSubMenu.utils'

import * as styles from './EmailSubMenu.module.css'

import SearchIcon from '../../../icons/search.svg'

function EmailSubMenu({ menuTitle, show, subMenu }) {
  const { accounts = EMPTY_OBJECT, items = EMPTY_LIST } =
    subMenu || EMPTY_OBJECT

  const { t } = useTranslation()
  const { setSubMenuAccounts } = useSubMenuActions()

  const [searchParams, setSearchParams] = useSearchParams()

  const [search, setSearch] = useState(EMPTY_STRING)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [isDataLoaded, setIsDataLoaded] = useState(false)

  const accountList = useMemo(
    () => getSubMenuEmailsAccountsList(accounts),
    [accounts]
  )

  useDefaultEmailParams({ accountList, accountsMenu: accounts })

  const sub = searchParams.get(SUBMENU_PARAM_KEY)
  const account = searchParams.get(SUBMENU_ACCOUNT_PARAM_KEY)
  const accountFromApiKey = searchParams.get(FILTER_NAME_ACCOUNT)

  //For optimization, the list of accounts is loaded twice, the first time without counters, the second time with them

  useEffect(() => {
    if (accountFromApiKey) {
      setSearchParams(
        prev => {
          prev.set(SUBMENU_ACCOUNT_PARAM_KEY, accountFromApiKey)
          prev.delete(FILTER_NAME_ACCOUNT)

          return prev
        },
        { replace: true }
      )
    }
  }, [accountFromApiKey])

  const setAccountsMenu = useCallback(params => {
    getEmailAccountsMenu(params).then(response => {
      setIsDataLoaded(true)
      setSubMenuAccounts(response.data)
    })
  }, [])

  useEffect(() => {
    setAccountsMenu(EMAIL_SUB_MENU_ACCOUNTS_MENU_NO_COUNT_PARAM)
  }, [])

  const handleSelectAccount = useCallback(
    item => {
      setSearchParams(params => {
        params.set(SUBMENU_ACCOUNT_PARAM_KEY, item[ITEM_KEYS.selectedKey])
        params.set(SUBMENU_PARAM_KEY, EMAIL_FOLDER_DEFAULT_VALUE)

        return params
      })
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (account && !isEmpty(accountList)) {
      const activeValue = getSubMenuEmailsActiveValue(account, accountList)

      if (activeValue) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- applies the resolved active account
        setSelectedAccount(activeValue)

        setStorageItem(
          LOCAL_STORAGE_MAILS_ACTIVE_ACCOUNT_KEY,
          activeValue[ITEM_KEYS.selectedKey]
        )
      } else {
        handleSelectAccount(getSubMenuEmailsDefaultValue(accountList))
      }
    }
  }, [account, accountFromApiKey, accountList])

  useEffect(() => {
    if (isDataLoaded) {
      setAccountsMenu()
    }
  }, [isDataLoaded])

  const sections = useMemo(
    () => getSubMenuEmailsSectionList(selectedAccount, accounts, search),
    [accounts, search, selectedAccount]
  )

  const handleChangeSearch = useCallback(({ target: { value } }) => {
    setSearch(value)
  }, [])

  const handleClearSearch = useCallback(() => setSearch(EMPTY_STRING), [])

  const handleSelectFolder = useCallback(
    item => {
      setSearchParams(params => {
        params.set([SUBMENU_PARAM_KEY], item)

        return params
      })
    },
    [setSearchParams]
  )

  const handleReconnect = useCallback(item => {
    reconnectEmailAccount(item[ITEM_KEYS.selectedKey]).then(response => {
      window.open(response?.data?.auth_url, HTML_LINK_TARGET.blank)
    })
  }, [])

  const listItemsOptions = {
    ...ITEM_KEYS,
    className: styles.availableItemsSection,
    isNeedReconnect: isSubMenuEmailNeedReconnect,
    items: sections,
    selected: selectedAccount,
    onClick: handleSelectAccount,
    onReconnect: handleReconnect,
  }

  if (!show || accountList.length === 0) {
    return null
  }

  const foldersProps = {
    apiKeys: FOLDER_ITEM_KEYS,
    counts: {
      [items[0]?.[FOLDER_ITEM_KEYS.value]]:
        selectedAccount?.[ITEM_KEYS.countKey],
    },
    isActive: item => item[FOLDER_ITEM_KEYS.value] === sub,
    items: subMenuEmailsFilterFolders(items, selectedAccount?.[EMAIL_CLIENT]),
    onClickItem: handleSelectFolder,
  }

  return (
    <SubMenuWrapper
      className={styles.wrapperSubmenu}
      qaName={QA_NAME_EMAIL_SUB_MENU}
    >
      <span className={styles.subTitle}>{menuTitle}</span>
      <SubMenuFoldersSection {...foldersProps} />
      <Divider className={styles.divider} />
      <div className={styles.SubMenuEmails}>
        <InputField
          afterIconProps={{
            Icon: SearchIcon,
          }}
          height={SEARCH_INPUT_HEIGHT}
          placeholder={t('Email.SearchPlaceholderBtnKey')}
          value={search}
          wrapperClassName={styles.input}
          onChange={handleChangeSearch}
          onClear={handleClearSearch}
        />
        {selectedAccount ? (
          <SubMenuAvailableItemsSection {...listItemsOptions} />
        ) : null}
      </div>
    </SubMenuWrapper>
  )
}

export default EmailSubMenu
